import React, { useEffect, useRef } from 'react';

/**
 * Protection anti-robots des formulaires d'inscription (sites web).
 * - Champ piège invisible : rempli seulement par les robots qui remplissent tout.
 * - Durée de remplissage : envoyée au serveur (refus sous 2,5 s).
 * - Captcha Cloudflare Turnstile : affiché seulement si le serveur fournit une clé de site.
 * L'état est gardé HORS du composant : les pages d'inscription sont recréées à chaque
 * rendu du parent, et il ne faut perdre ni l'heure d'ouverture ni le jeton du captcha.
 */
const store = {}; // formKey → { startedAt, honeypot, token, widgetId }
let configPromise = null;
let scriptPromise = null;

const entry = (formKey) => {
  if (!store[formKey]) store[formKey] = { startedAt: Date.now(), honeypot: "", token: null, widgetId: null };
  return store[formKey];
};

function loadConfig(baseURL) {
  if (!configPromise) {
    configPromise = fetch(`${baseURL}/turnstile-config`)
      .then(r => r.json())
      .then(d => (d && d.site_key) || null)
      .catch(() => null);
  }
  return configPromise;
}

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/** Champs à joindre à la requête d'inscription. */
export function botFields(formKey) {
  const e = entry(formKey);
  return { website: e.honeypot, form_ms: Date.now() - e.startedAt, turnstile_token: e.token || undefined };
}

/** Après une tentative (réussie ou non) : le jeton Turnstile est à usage unique. */
export function resetBotShield(formKey) {
  const e = entry(formKey);
  e.token = null;
  try { if (window.turnstile && e.widgetId != null) window.turnstile.reset(e.widgetId); } catch (_) {}
}

/** À placer dans le <form> d'inscription, juste avant le bouton d'envoi. */
export default function BotShield({ formKey, baseURL }) {
  const box = useRef(null);
  entry(formKey); // démarre le chronomètre à la première apparition du formulaire

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const siteKey = await loadConfig(baseURL);
      if (!siteKey || cancelled || !box.current) return;
      try {
        const ts = await loadScript();
        if (cancelled || !box.current) return;
        const e = entry(formKey);
        e.widgetId = ts.render(box.current, {
          sitekey: siteKey,
          language: 'fr',
          callback: (token) => { e.token = token; },
          'expired-callback': () => { e.token = null; },
          'error-callback': () => { e.token = null; },
        });
      } catch (_) { /* script bloqué : le serveur décidera */ }
    })();
    return () => {
      cancelled = true;
      const e = entry(formKey);
      try { if (window.turnstile && e.widgetId != null) window.turnstile.remove(e.widgetId); } catch (_) {}
      e.widgetId = null;
    };
  }, [formKey, baseURL]);

  return (
    <>
      {/* Piège : invisible pour les humains (hors écran, ignoré au clavier et par les lecteurs d'écran). */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
        <label>Ne pas remplir ce champ
          <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue=""
            onChange={(ev) => { entry(formKey).honeypot = ev.target.value; }} />
        </label>
      </div>
      <div ref={box} style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }} />
    </>
  );
}
